import type {
  KPIDeltaPresentation,
  KPIDeltaSemantic,
  KPIDirectionality,
} from './KPIBanner';

/** Badge variant for delta chip (HEN Badge has no dedicated success/error variants). */
export function getDeltaBadgeVariant(
  semantic: KPIDeltaSemantic,
): 'neutral' | 'outline' {
  return semantic === 'neutral' ? 'neutral' : 'outline';
}

/** Extra classes on `Badge` for semantic delta colors when using `variant="outline"`. */
export function getDeltaBadgeClassName(semantic: KPIDeltaSemantic): string {
  if (semantic === 'neutral') return 'text-subtext';
  if (semantic === 'positive')
    return 'border-success-500 text-success-500 bg-transparent';
  return 'border-error-500 text-error-500 bg-transparent';
}

function isNeutralDelta(delta: number | null | undefined): boolean {
  if (delta == null) return true;
  if (typeof delta !== 'number' || Number.isNaN(delta)) return true;
  return delta === 0;
}

/**
 * Maps raw delta sign and KPI directionality to semantic good/bad/neutral for styling.
 */
export function deltaToSemantic(
  delta: number | null,
  directionality: KPIDirectionality,
): KPIDeltaSemantic {
  if (isNeutralDelta(delta)) return 'neutral';
  const sign = delta! > 0 ? 1 : -1;
  if (directionality === 'higher-is-better') {
    return sign > 0 ? 'positive' : 'negative';
  }
  return sign > 0 ? 'negative' : 'positive';
}

/**
 * Mirror of {@link deltaToSemantic} but driven by the displayed value's own
 * sign, for tiles that have no separate comparator/delta. Use when a
 * `KPITile`'s headline number itself carries directional meaning (signed
 * metric, variance, NPS) and the consumer asks for `colorValue:
 * 'auto-by-value'`.
 *
 * Mirrors `SeriesColorConfig.semanticColor: 'auto-by-value'` from
 * WaterfallChart — same vocabulary, same semantics.
 */
export function valueToSemantic(
  value: number | null | undefined,
  directionality: KPIDirectionality,
): KPIDeltaSemantic {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) {
    return 'neutral';
  }
  const positive = value > 0;
  const goodIsHigher = directionality !== 'lower-is-better';
  return positive === goodIsHigher ? 'positive' : 'negative';
}

/**
 * Parse the `KPITileProps.value` display string (already formatted for
 * presentation — `'−42'`, `'1,284'`, `'$1,234.56'`, `'42%'`) back to a
 * raw numeric so {@link valueToSemantic} can read its sign for
 * `colorValue: 'auto-by-value'`.
 *
 * Returns `null` for unparseable inputs (`'—'`, `'N/A'`, `''`, …) so the
 * caller falls back to neutral tone.
 *
 * Internal helper — exported for unit-test coverage of the parser edge
 * cases. Not part of the public hen-ui surface.
 *
 * Limitations:
 * - Assumes en-US formatting (`,` thousands separator, `.` decimal).
 *   European-style `1.234,56` is not supported.
 * - Strips currency symbols, percent signs, and whitespace; the residual
 *   characters must form a valid `Number()` literal.
 */
export function parseDisplayValue(value: string): number | null {
  if (typeof value !== 'string') return null;
  const normalized = value
    .replace(/\u2212/g, '-') // Unicode minus → ASCII hyphen-minus
    .replace(/[^\d.\-+eE]/g, ''); // strip everything except digits, sign, decimal, scientific notation
  if (normalized === '' || normalized === '-' || normalized === '+') {
    return null;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

const semanticTone: Record<KPIDeltaSemantic, string> = {
  positive: 'text-success-500',
  negative: 'text-error-500',
  neutral: 'text-subtext',
};

/**
 * Arrow direction follows the numeric delta sign (↑ for positive Δ, ↓ for negative Δ).
 * Color follows {@link deltaToSemantic} (good/bad vs directionality).
 */
export function getDeltaPresentation(
  delta: number | null,
  directionality: KPIDirectionality,
): KPIDeltaPresentation {
  if (isNeutralDelta(delta)) {
    return {
      semantic: 'neutral',
      arrowIcon: null,
      toneClassName: semanticTone.neutral,
    };
  }
  const semantic = deltaToSemantic(delta, directionality);
  const arrowIcon: 'arrow-up' | 'arrow-down' =
    (delta as number) > 0 ? 'arrow-up' : 'arrow-down';
  return {
    semantic,
    arrowIcon,
    toneClassName: semanticTone[semantic],
  };
}

export function defaultFormatDeltaValue(delta: number | null): string {
  if (delta == null || Number.isNaN(delta)) return '—';
  if (delta === 0) return '0';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta}`;
}
